import { buildSearchIntent, displaySearchTerm, getVehicleChoices, normalizeSearchURL, replaceSearchVehicle } from '@theme/fastutv-search-intent';

// A session preference only: it narrows shopping results and never asserts fitment.
const storageKey = 'fastutv-shopping-vehicle-v1';
const choices = getVehicleChoices();
const nonVehicle = new Set(['racing-fuel', 'utv-apparel-merch-pro-r-turbo-r']);
let selected = null;
const rewrittenAnchors = new Map();
const collectionPath = url => url.pathname.match(/^(.*\/collections\/)([^/]+)(?:\/(.*))?$/);
const vehicle = intent => !intent || intent.conflictingMake ? null : intent.model ? { id: intent.model.id, label: intent.model.make + ' ' + intent.model.label } : intent.make ? { id: intent.make.id, label: intent.make.label } : null;
const known = value => {
  const intent = buildSearchIntent(value);
  return !intent.advanced && !intent.conflictingMake && !intent.residual ? vehicle(intent) : null;
};
function saved() {
  try {
    const data = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    const choice = choices.models.find(item => item.id === data?.id) || choices.makes.find(item => item.id === data?.id);
    return choice ? known(choice.make ? choice.make + ' ' + choice.label : choice.label) : null;
  } catch { return null; }
}
function remember(value) {
  if (selected?.id !== value?.id) {
    for (const [anchor, originalHref] of rewrittenAnchors) anchor.setAttribute('href', originalHref);
    rewrittenAnchors.clear();
  }
  selected = value;
  try { if (value) sessionStorage.setItem(storageKey, JSON.stringify(value)); else sessionStorage.removeItem(storageKey); } catch { /* Shopping works without storage. */ }
  document.documentElement.dataset.fastutvVehicle = value?.label || '';
  syncLabels();
}
function syncLabels() {
  const current = new URL(location.href);
  const collection = collectionPath(current);
  const root = collection ? collection[1] + 'all' : (document.documentElement.lang.startsWith('es') ? '/es' : '') + '/collections/all';
  for (const node of document.querySelectorAll('[data-fastutv-vehicle-context]')) {
    node.hidden = !selected;
    const label = node.querySelector('[data-fastutv-context-label]'); if (label) label.textContent = selected?.label || '';
    const link = node.querySelector('[data-fastutv-context-link]');
    if (link && selected) { const url = new URL(root, location.origin); url.searchParams.set('fastutv_vehicle', selected.label); const preview = current.searchParams.get('preview_theme_id'); if (preview) url.searchParams.set('preview_theme_id', preview); link.href = url.pathname + url.search; }
  }
}
function initialize() {
  const url = new URL(location.href), path = collectionPath(url);
  const raw = /\/search$/.test(url.pathname) ? buildSearchIntent(displaySearchTerm(url)) : null;
  if (raw?.conflictingMake) { remember(null); return; }
  if (url.searchParams.get('fastutv_vehicle') === 'all' && !vehicle(raw)) { remember(null); return; }
  let current = known(url.searchParams.get('fastutv_vehicle') || '');
  if (vehicle(raw)) current = vehicle(raw);
  else if (url.searchParams.has('fastutv_vehicle') && !current) { remember(null); return; }
  let config = null;
  try { config = JSON.parse(document.querySelector('[data-discovery-config]')?.textContent || 'null'); } catch { /* Native fallback remains available. */ }
  if (!current && path && !nonVehicle.has(path[2])) {
    const tag = config?.tags?.find(value => /^Browse Model:/i.test(value)) || config?.tags?.find(value => /^Browse Make:/i.test(value));
    const intent = buildSearchIntent(tag ? tag.replace(/^Browse (?:Model|Make):\s*/i, '') : decodeURIComponent(path[2]).replaceAll('-', ' '));
    if (intent.model || intent.make) current = vehicle(intent);
  }
  remember(current || saved());
  if (selected && !raw?.advanced && ((path && !nonVehicle.has(path[2])) || (raw && !current))) {
    url.searchParams.set('fastutv_vehicle', selected.label);
    history.replaceState(history.state, '', url);
  }
}

// Capture before the shared submit normalizer, retaining the shopper's words.
window.addEventListener('submit', event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-fastutv-search-form]')) return;
  const input = form.querySelector('input[name="q"]'); if (!input) return;
  const intent = buildSearchIntent(input.value);
  const own = vehicle(intent);
  if (own && !intent.conflictingMake) remember(own);
  if (intent.conflictingMake) remember(null);
  const context = intent.conflictingMake ? null : own || (!intent.advanced ? selected : null);
  let hidden = form.querySelector('input[name="fastutv_vehicle"]');
  if (!hidden) { hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.name = 'fastutv_vehicle'; form.append(hidden); }
  hidden.value = context?.label || 'all';
}, true);
document.addEventListener('fastutv:vehicle-change', event => remember(known(event.detail?.label || '')));
document.addEventListener('click', event => {
  const clear = event.target.closest('[data-fastutv-context-clear]');
  if (clear) {
    event.preventDefault(); remember(null);
    let url = new URL(location.href); const path = collectionPath(url);
    if (/\/search$/.test(url.pathname)) url = normalizeSearchURL(url, replaceSearchVehicle(displaySearchTerm(url), ''));
    else if (path) {
      const own = buildSearchIntent(decodeURIComponent(path[2]).replaceAll('-', ' '));
      const tags = (path[3] || '').split('+').filter(tag => !/^browse-(?:make|model)-/.test(tag));
      url.pathname = path[1] + (own.model || own.make ? 'all' : path[2]) + (tags.length ? '/' + tags.join('+') : '');
    } else return;
    url.searchParams.set('fastutv_vehicle', 'all'); url.searchParams.delete('page'); url.searchParams.delete('fastutv_page'); location.assign(url.href); return;
  }
  const anchor = event.target.closest('a[href]');
  if (!anchor || !selected || anchor.hasAttribute('download')) return;
  const originalHref = rewrittenAnchors.get(anchor) ?? anchor.getAttribute('href');
  const url = new URL(originalHref, location.href), path = collectionPath(url);
  if (url.origin !== location.origin || !path || nonVehicle.has(path[2]) || url.searchParams.has('fastutv_vehicle')) return;
  const target = buildSearchIntent(decodeURIComponent(path[2] + ' ' + (path[3] || '')).replaceAll('-', ' '));
  if (target.make || target.model) return;
  rewrittenAnchors.set(anchor, originalHref);
  url.searchParams.set('fastutv_vehicle', selected.label); anchor.href = url.href;
}, true);
initialize();
window.addEventListener('popstate', initialize);
window.addEventListener('pageshow', event => event.persisted ? initialize() : syncLabels());
document.addEventListener('shopify:section:load', syncLabels);
